import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Col,
  Button,
  FormFeedback,
  Spinner,
  Form,
  Input,
  Label,
} from 'reactstrap';
import Icon from '@mdi/react';
import { mdiSquareEditOutline } from '@mdi/js';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  initialValues,
  validationSchema,
  transformLocalAddressData,
  transformPermAddressData,
} from './constant';
import {
  fetchEmployeeCountryCodeList,
  fetchEmployeeDetails,
  updateContactDetails,
} from '../../../store/apis/employeeService';
import '../index.scss';
import * as icons from '../../../assets';

function ContactInfo() {
  const [editLocal, setEditLocal] = useState(false);
  const [editPermanent, setEditPermanent] = useState(false);
  const queryClient = useQueryClient();

  const {
    control: localControl,
    handleSubmit: handleLocalSubmit,
    reset: localReset,
    formState: { errors: localErrors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: { ...initialValues, addressType: 'LOCAL_ADDRESS' },
  });

  const {
    control: permControl,
    handleSubmit: handlePermSubmit,
    reset: permReset,
    formState: { errors: permErrors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: { ...initialValues, addressType: 'PERM_ADDRESS' },
  });

  const { data: employeeLocalInfo, isLoading: localInfoLoading } = useQuery({
    queryKey: ['employeeLocalInfo'],
    queryFn: () => fetchEmployeeDetails({ type: 'LOCAL_ADDRESS' }),
  });

  const { data: employeePermInfo, isLoading: permInfoLoading } = useQuery({
    queryKey: ['employeePermInfo'],
    queryFn: () => fetchEmployeeDetails({ type: 'PERM_ADDRESS' }),
  });

  const { data: countryList } = useQuery({
    queryKey: ['contactCountryList'],
    queryFn: () => fetchEmployeeCountryCodeList({ codeType: 'COUNTRY' }),
  });

  const handleEditLocalToggle = () => {
    setEditLocal(!editLocal);
  };

  const handleEditPermanentToggle = () => {
    setEditPermanent(!editPermanent);
  };

  useEffect(() => {
    if (employeeLocalInfo?.[0]) {
      const transformed = transformLocalAddressData(employeeLocalInfo[0]);
      localReset(transformed);
    }
  }, [employeeLocalInfo, localReset]);

  useEffect(() => {
    if (employeePermInfo?.[0]) {
      const transformed = transformPermAddressData(employeePermInfo[0]);
      permReset(transformed);
    }
  }, [employeePermInfo, permReset]);

  const { mutate: updateContactInfo } = useMutation({
    mutationFn: data => updateContactDetails(data),
    onSuccess: (_, variables) => {
      toast.success('Contact info updated successfully', {
        className: 'toast-success',
      });

      // Invalidate and refetch relevant address info
      if (variables.addressType === 'LOCAL_ADDRESS') {
        setEditLocal(false);
        queryClient.invalidateQueries({ queryKey: ['employeeLocalInfo'] });
      } else if (variables.addressType === 'PERM_ADDRESS') {
        setEditPermanent(false);
        queryClient.invalidateQueries({ queryKey: ['employeePermInfo'] });
      }
    },
    onError: error => {
      toast.error(
        error?.response?.data?.message ||
          'Something went wrong. Please contact administrator.',
        {
          className: 'toast-error',
        },
      );
    },
  });

  const onSubmit = data => {
    updateContactInfo(data);
  };

  const isTrimmedEmpty = str => typeof str === 'string' && str.trim() === '';

  const getCountryDescription = countryCode => {
    if (isTrimmedEmpty(countryCode)) return '-';
    const country = countryList?.find(c => c?.Code === countryCode);
    return country?.Description || countryCode;
  };

  return (
    <div className="profile-component d-flex flex-row align-items-start gap-4 p-3">
      <Col lg={4}>
        <Card className="profile-card">
          <CardHeader className="d-flex flex-row align-items-center justify-content-between contact-card-header py-2">
            <div className="d-flex flex-row align-items-center gap-2">
              <div className="user-profile-header-icon">
                <img
                  src={icons.iconsContactIcon}
                  alt="Contact Icon"
                  height="15px"
                />
              </div>
              <div>
                <span className="fw-semibold fs-5">Local Address</span>
              </div>
            </div>
            <div>
              {editLocal ? (
                <div className="d-flex flex-row align-items-center gap-2">
                  <Button
                    className="custom-danger-btn"
                    onClick={handleEditLocalToggle}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="custom-success-btn"
                    onClick={handleLocalSubmit(onSubmit)}
                  >
                    Update
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleEditLocalToggle}
                  className="cursor-pointer"
                  style={{ background: 'none', border: 'none' }}
                >
                  <Icon
                    path={mdiSquareEditOutline}
                    size={0.7}
                    color="#017EBD"
                  />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {(() => {
              if (editLocal) {
                return (
                  <Form className="p-0" onSubmit={handleLocalSubmit(onSubmit)}>
                    <div className="d-flex flex-column gap-2 py-3">
                      <div className="d-flex flex-column px-3">
                        <Label className="profile-label mb-1">
                          Address Line 1<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="addrLn1"
                          control={localControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Address Line 1"
                              className={`form-control external-filter-input ${
                                localErrors.addrLn1 && 'is-invalid'
                              }`}
                            />
                          )}
                        />
                        {localErrors.addrLn1 && (
                          <FormFeedback className="text-danger">
                            {localErrors.addrLn1.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Address Line 2
                        </Label>
                        <Controller
                          name="addrLn2"
                          control={localControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Address Line 2"
                              className={`form-control external-filter-input ${
                                localErrors.addrLn2 && 'is-invalid'
                              }`}
                            />
                          )}
                        />
                        {localErrors.addrLn2 && (
                          <FormFeedback className="text-danger">
                            {localErrors.addrLn2.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Address Line 3
                        </Label>
                        <Controller
                          name="addrLn3"
                          control={localControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Address Line 3"
                              className={`form-control external-filter-input ${
                                localErrors.addrLn3 && 'is-invalid'
                              }`}
                            />
                          )}
                        />
                        {localErrors.addrLn3 && (
                          <FormFeedback className="text-danger">
                            {localErrors.addrLn3.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Country<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="country"
                          control={localControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="select"
                              placeholder="Enter Address Line 1"
                              className={`form-select external-filter-input ${
                                localErrors.country && 'is-invalid'
                              }`}
                            >
                              <option value="">Select Country</option>
                              {countryList?.map(country => (
                                <option
                                  key={country?.Code}
                                  value={country?.Code}
                                >
                                  {country?.Description}
                                </option>
                              ))}
                            </Input>
                          )}
                        />
                        {localErrors.country && (
                          <FormFeedback className="text-danger">
                            {localErrors.country.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Telephone No.<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="telephoneNo"
                          control={localControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Telephone No."
                              type="number"
                              className={`form-control external-filter-input ${localErrors.telephoneNo && 'is-invalid'}`}
                            />
                          )}
                        />
                        {localErrors.telephoneNo && (
                          <FormFeedback className="text-danger">
                            {localErrors.telephoneNo.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Mobile No.<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="mobileNo"
                          control={localControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Mobile No."
                              type="number"
                              className={`form-control external-filter-input ${localErrors.mobileNo && 'is-invalid'}`}
                            />
                          )}
                        />
                        {localErrors.mobileNo && (
                          <FormFeedback className="text-danger">
                            {localErrors.mobileNo.message}
                          </FormFeedback>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-column p-3 emergency-contact-wrapper">
                      <div className="emergency-contact-footer mb-3">
                        Emergency Contact
                      </div>

                      <div className="d-flex flex-column gap-3">
                        <div className="d-flex flex-column">
                          <Label className="profile-label mb-1">
                            Emergency Contact Person
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="emergencyConPerson"
                            control={localControl}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Enter Emergency Contact Person"
                                type="text"
                                className={`form-control external-filter-input ${localErrors.emergencyConPerson && 'is-invalid'}`}
                              />
                            )}
                          />
                          {localErrors.emergencyConPerson && (
                            <FormFeedback className="text-danger">
                              {localErrors.emergencyConPerson.message}
                            </FormFeedback>
                          )}
                        </div>
                        <div className="emergency-contact-separator" />
                        <div className="d-flex flex-column">
                          <Label className="profile-label mb-1">
                            Emergency Contact No.
                            <span className="text-danger">*</span>
                          </Label>
                          <Controller
                            name="emergencyConNo"
                            control={localControl}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Enter Emergency Contact No."
                                type="number"
                                className={`form-control external-filter-input ${localErrors.emergencyConNo && 'is-invalid'}`}
                              />
                            )}
                          />
                          {localErrors.emergencyConNo && (
                            <FormFeedback className="text-danger">
                              {localErrors.emergencyConNo.message}
                            </FormFeedback>
                          )}
                        </div>
                      </div>
                    </div>
                  </Form>
                );
              }
              if (localInfoLoading) {
                return <Spinner color="primary" size="sm" type="grow" />;
              }
              return (
                <div>
                  <div className="d-flex flex-column gap-3 py-3">
                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Address</span>
                      <span className="profile-value d-flex flex-column">
                        <span>
                          {isTrimmedEmpty(employeeLocalInfo?.[0]?.LocalAddrLn1)
                            ? '-'
                            : employeeLocalInfo?.[0]?.LocalAddrLn1}
                        </span>
                        <span>
                          {isTrimmedEmpty(employeeLocalInfo?.[0]?.LocalAddrLn2)
                            ? '-'
                            : employeeLocalInfo?.[0]?.LocalAddrLn2}
                        </span>
                        <span>
                          {isTrimmedEmpty(employeeLocalInfo?.[0]?.LocalAddrLn3)
                            ? '-'
                            : employeeLocalInfo?.[0]?.LocalAddrLn3}
                        </span>
                      </span>
                    </div>

                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Country</span>
                      <span className="profile-value">
                        {getCountryDescription(
                          employeeLocalInfo?.[0]?.LocalCountry,
                        )}
                      </span>
                    </div>
                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Telephone Number</span>
                      <span className="profile-value">
                        {isTrimmedEmpty(employeeLocalInfo?.[0]?.LocalTelephone)
                          ? '-'
                          : employeeLocalInfo?.[0]?.LocalTelephone}
                      </span>
                    </div>
                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Mobile Number</span>
                      <span className="profile-value">
                        {isTrimmedEmpty(employeeLocalInfo?.[0]?.LocalMobile)
                          ? '-'
                          : employeeLocalInfo?.[0]?.LocalMobile}
                      </span>
                    </div>
                  </div>
                  <div className="emergency-contact-wrapper p-1">
                    <div className="emergency-contact-footer">
                      Emergency Contact
                    </div>
                    <div className="d-flex flex-column py-2 px-2">
                      <span className="profile-label">
                        Emergency Contact Person
                      </span>
                      <span className="profile-value">
                        {isTrimmedEmpty(
                          employeeLocalInfo?.[0]?.EmergencyConPerson,
                        )
                          ? '-'
                          : employeeLocalInfo?.[0]?.EmergencyConPerson}
                      </span>
                    </div>
                    <div className="emergency-contact-separator" />
                    <div className="d-flex flex-column py-2 px-2">
                      <span className="profile-label">
                        Emergency Contact Number
                      </span>
                      <span className="profile-value">
                        {isTrimmedEmpty(employeeLocalInfo?.[0]?.EmergencyConNo)
                          ? '-'
                          : employeeLocalInfo?.[0]?.EmergencyConNo}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardBody>
        </Card>
      </Col>
      <Col lg={4}>
        <Card className="profile-card">
          <CardHeader className="d-flex flex-row align-items-center justify-content-between contact-card-header py-2">
            <div className="d-flex flex-row align-items-center gap-2">
              <div className="user-profile-header-icon">
                <img src={icons.iconsGlobe} alt="Perm Icon" height="15px" />
              </div>
              <div>
                <span className="fw-semibold fs-5">
                  Permanent Address (Home Country)
                </span>
              </div>
            </div>
            {editPermanent ? (
              <div className="d-flex flex-row align-items-center gap-2">
                <Button
                  className="custom-danger-btn"
                  onClick={handleEditPermanentToggle}
                >
                  Cancel
                </Button>
                <Button
                  className="custom-success-btn"
                  onClick={handlePermSubmit(onSubmit)}
                >
                  Update
                </Button>
              </div>
            ) : (
              <Button
                className="cursor-pointer"
                onClick={handleEditPermanentToggle}
                style={{ background: 'none', border: 'none' }}
              >
                <Icon path={mdiSquareEditOutline} size={0.7} color="#017EBD" />
              </Button>
            )}
          </CardHeader>
          <CardBody
            className="p-0"
            style={{ borderRadius: '0px 0px 10px 10px' }}
          >
            {(() => {
              if (editPermanent) {
                return (
                  <Form className="p-0" onSubmit={handlePermSubmit(onSubmit)}>
                    <div className="d-flex flex-column gap-2 py-3">
                      <div className="d-flex flex-column px-3">
                        <Label className="profile-label mb-1">
                          Address Line 1<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="addrLn1"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Address Line 1"
                              className={`form-control external-filter-input ${
                                permErrors.addrLn1 && 'is-invalid'
                              }`}
                            />
                          )}
                        />
                        {permErrors.addrLn1 && (
                          <FormFeedback className="text-danger">
                            {permErrors.addrLn1.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3">
                        <Label className="profile-label mb-1">
                          Address Line 2
                        </Label>
                        <Controller
                          name="addrLn2"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Address Line 2"
                              className={`form-control external-filter-input ${
                                permErrors.addrLn2 && 'is-invalid'
                              }`}
                            />
                          )}
                        />
                        {permErrors.addrLn2 && (
                          <FormFeedback className="text-danger">
                            {permErrors.addrLn2.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Address Line 3
                        </Label>
                        <Controller
                          name="addrLn3"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Address Line 3"
                              className={`form-control external-filter-input ${
                                permErrors.addrLn1 && 'is-invalid'
                              }`}
                            />
                          )}
                        />
                        {permErrors.addrLn3 && (
                          <FormFeedback className="text-danger">
                            {permErrors.addrLn3.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Country<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="country"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="select"
                              className={`form-select external-filter-input ${
                                permErrors.addrLn1 && 'is-invalid'
                              }`}
                            >
                              <option value="">Select Country</option>
                              {countryList?.map(country => (
                                <option
                                  key={country?.Code}
                                  value={country?.Code}
                                >
                                  {country?.Description}
                                </option>
                              ))}
                            </Input>
                          )}
                        />
                        {permErrors.country && (
                          <FormFeedback className="text-danger">
                            {permErrors.country.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Telephone No.<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="telephoneNo"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Telephone No."
                              type="number"
                              className={`form-control external-filter-input ${permErrors.telephoneNo && 'is-invalid'}`}
                            />
                          )}
                        />
                        {permErrors.telephoneNo && (
                          <FormFeedback className="text-danger">
                            {permErrors.telephoneNo.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="d-flex flex-column px-3 ">
                        <Label className="profile-label mb-1">
                          Mobile No.<span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="mobileNo"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Mobile No."
                              type="number"
                              className={`form-control external-filter-input ${permErrors.mobileNo && 'is-invalid'}`}
                            />
                          )}
                        />
                        {permErrors.mobileNo && (
                          <FormFeedback className="text-danger">
                            {permErrors.mobileNo.message}
                          </FormFeedback>
                        )}
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-3 p-3 emergency-contact-wrapper">
                      <div className="emergency-contact-footer">
                        Emergency Contact
                      </div>
                      <div className="d-flex flex-column">
                        <Label className="profile-label mb-1">
                          Emergency Contact Person
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="emergencyConPerson"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Emergency Contact Person"
                              type="text"
                              className={`form-control external-filter-input ${permErrors.emergencyConPerson && 'is-invalid'}`}
                            />
                          )}
                        />
                        {permErrors.emergencyConPerson && (
                          <FormFeedback className="text-danger">
                            {permErrors.emergencyConPerson.message}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="emergency-contact-separator" />
                      <div className="d-flex flex-column">
                        <Label className="profile-label mb-1">
                          Emergency Contact No.
                          <span className="text-danger">*</span>
                        </Label>
                        <Controller
                          name="emergencyConNo"
                          control={permControl}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Enter Emergency Contact No."
                              type="number"
                              className={`form-control external-filter-input ${permErrors.emergencyConNo && 'is-invalid'}`}
                            />
                          )}
                        />
                        {permErrors.emergencyConNo && (
                          <FormFeedback className="text-danger">
                            {permErrors.emergencyConNo.message}
                          </FormFeedback>
                        )}
                      </div>
                    </div>
                  </Form>
                );
              }
              if (permInfoLoading) {
                return <Spinner color="primary" size="sm" type="grow" />;
              }
              return (
                <div>
                  <div className="d-flex flex-column gap-3 py-3">
                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Address</span>
                      <span className="profile-value d-flex flex-column">
                        <span>
                          {isTrimmedEmpty(employeePermInfo?.[0]?.PermAddrLn1)
                            ? '-'
                            : employeePermInfo?.[0]?.PermAddrLn1}
                        </span>
                        <span>
                          {isTrimmedEmpty(employeePermInfo?.[0]?.PermAddrLn2)
                            ? '-'
                            : employeePermInfo?.[0]?.PermAddrLn2}
                        </span>
                        <span>
                          {isTrimmedEmpty(employeePermInfo?.[0]?.PermAddrLn3)
                            ? '-'
                            : employeePermInfo?.[0]?.PermAddrLn3}
                        </span>
                      </span>
                    </div>

                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Country</span>
                      <span className="profile-value">
                        {getCountryDescription(
                          employeePermInfo?.[0]?.PermCountry,
                        )}
                      </span>
                    </div>
                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Telephone Number</span>
                      <span className="profile-value">
                        {isTrimmedEmpty(employeePermInfo?.[0]?.PermTelephone)
                          ? '-'
                          : employeePermInfo?.[0]?.PermTelephone}
                      </span>
                    </div>
                    <div className="d-flex flex-column px-3">
                      <span className="profile-label">Mobile Number</span>
                      <span className="profile-value">
                        {isTrimmedEmpty(employeePermInfo?.[0]?.PermMobile)
                          ? '-'
                          : employeePermInfo?.[0]?.PermMobile}
                      </span>
                    </div>
                  </div>
                  <div className="emergency-contact-wrapper p-1">
                    <div className="emergency-contact-footer">
                      Emergency Contact
                    </div>
                    <div className="d-flex flex-column py-2 px-2">
                      <span className="profile-label">
                        Emergency Contact Person
                      </span>
                      <span className="profile-value">
                        {isTrimmedEmpty(
                          employeePermInfo?.[0]?.EmergencyConPerson,
                        )
                          ? '-'
                          : employeePermInfo?.[0]?.EmergencyConPerson}
                      </span>
                    </div>
                    <div className="emergency-contact-separator" />
                    <div className="d-flex flex-column py-2 px-2">
                      <span className="profile-label">
                        Emergency Contact Number
                      </span>
                      <span className="profile-value">
                        {isTrimmedEmpty(employeePermInfo?.[0]?.EmergencyConNo)
                          ? '-'
                          : employeePermInfo?.[0]?.EmergencyConNo}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}

export default ContactInfo;
